import { useEffect, useState } from "react";
import { LeadFormApi, StepConfig } from "./steps";
import stepsConfig from "./steps";
import "./styles.css";
import { getUTMParameters, UTMParams } from "../utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import ErrorBoundary from "../components/ErrorBoundary";
import { LeadFormContext } from "../context/leadform";
import { Progress } from "../components/Progress";
import {
  toTrackingName,
  trackStepView,
  trackValidationError,
  trackStepComplete,
  defaultDetectFailedField,
} from "./tracking";

const Leadform = () => {
  const debug = false;

  const [steps, setSteps] = useState<Array<StepConfig>>(stepsConfig);
  const [step, setStep] = useState<number>(0);
  const [responses, setResponses] = useState<{ [key: string]: any }>({});
  const [utms, setUTMs] = useState<UTMParams>({});
  const [userPath, setUserPath] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [sessionKey, setSessionKey] = useState<string>("");
  const [stepStartTime, setStepStartTime] = useState<number>(Date.now());

  const setStepFunc = (step: number) => {
    setStep(step);
    if (window.innerWidth < 767) {
      setTimeout(() => {
        window.scrollTo({
          top: 0,
        });
      }, 200);
    }
  };

  const formApi: LeadFormApi = {
    step,
    setStep: setStepFunc,
    responses,
    setResponses,
    utms,
    setUTMs,
    userPath,
    setUserPath,
    sessionKey,
    setSessionKey,
    loading,
    setLoading,
  };

  useEffect(() => {
    // Get UTMs From URL or Session Storage
    const utms = getUTMParameters();
    setUTMs(utms);

    setResponses({
      ...responses,
      ...utms,
    });

    // Get user path if available
    const user_path = window.sessionStorage.getItem("user_path") || "[]";
    const parsed_path = JSON.parse(user_path)
      .map((item: string) => item)
      .join("\n");
    setUserPath(parsed_path);
  }, []);

  // Track step view
  useEffect(() => {
    trackStepView({
      stepIndex: step,
      stepName: steps[step] ? toTrackingName(steps[step].name) : "unknown",
      totalSteps: steps.length,
    });
    setStepStartTime(Date.now());
  }, [step]);

  const renderStep = () => {
    const currentStep = steps[step];
    const Component = steps[step].component;
    return (
      <Component
        title={steps[step].title}
        description={steps[step].description}
        statekey={steps[step].statekey}
        updateValue={setResponses}
        {...steps[step].additionalProps}
      />
    );
  };

  const nextStep = () => {
    const currentStep = steps[step];
    const stepName = toTrackingName(currentStep.name);

    // Check validation
    if (currentStep.validateFunc && !currentStep.validateFunc(formApi)) {
      const timeOnStep = Math.round((Date.now() - stepStartTime) / 1000);
      const { fieldName, errorType } = currentStep.detectFailedField
        ? currentStep.detectFailedField(responses)
        : defaultDetectFailedField(responses);

      trackValidationError({
        stepIndex: step,
        stepName,
        fieldName,
        errorType,
        timeOnStepSeconds: timeOnStep,
      });
      return;
    }

    const timeOnStep = Math.round((Date.now() - stepStartTime) / 1000);
    const nextStepName = steps[step + 1]
      ? toTrackingName(steps[step + 1].name)
      : "unknown";
    const extraParams = currentStep.getStepCompleteExtraParams
      ? currentStep.getStepCompleteExtraParams(responses)
      : undefined;

    trackStepComplete({
      stepIndex: step,
      stepName,
      nextStepName,
      totalSteps: steps.length,
      timeOnStepSeconds: timeOnStep,
      extraParams,
    });

    if (currentStep.nextStep && currentStep.statekey) {
      currentStep.nextStep({
        value: responses[currentStep.statekey],
        form: formApi,
      });
    } else {
      setStepFunc(step + 1);
    }
  };

  const prevStep = () => {
    const currentStep = steps[step];

    if (currentStep.prevStep) {
      currentStep.prevStep({ form: formApi });
    } else {
      setStepFunc(step - 1);
    }
  };

  const renderNextButton = () => {
    const currentStep = steps[step];
    if (currentStep.next === false) {
      return;
    }

    const validate =
      currentStep.validate !== undefined ? currentStep.validate : true;
    let isDisabled = false;

    if (validate && currentStep.statekey) {
      isDisabled =
        responses[currentStep.statekey] === "" ||
        responses[currentStep.statekey] === undefined;
    }

    if (currentStep.validateFunc !== undefined) {
      // TODO Update to set step
      isDisabled = !currentStep.validateFunc(formApi);
    }

    const label = currentStep.nextLabel || "Continue";
    const pulse = currentStep.pulse;
    return (
      <button
        id="nextButton"
        className={`Primary ${pulse ? "pulse" : ""}`}
        disabled={isDisabled}
        onClick={() => nextStep()}
      >
        {label}
      </button>
    );
  };

  const renderBackButton = () => {
    const currentStep = steps[step];

    if (currentStep.back === false || step === 0) {
      return;
    }

    return (
      <button id="backButton" className="Secondary" onClick={() => prevStep()}>
        Back
      </button>
    );
  };

  const renderAdditionalButtons = () => {
    const currentStep = steps[step];
    if (
      currentStep.additionalButtons &&
      currentStep.additionalButtons.length > 0
    ) {
      return currentStep.additionalButtons.map((button, index) => (
        <button className="Secondary" key={index} onClick={button.onClick}>
          {button.label}
        </button>
      ));
    }
    return;
  };

  const updateValue = (
    name: string,
    value: string | { [key: string]: any },
  ) => {
    if (typeof value === "object") {
      setResponses({
        ...responses,
        ...value,
      });
    } else {
      setResponses({
        ...responses,
        [name]: value,
      });
    }
  };

  const printResponses = () => {
    return (
      <code className="debug">
        Debug information:
        <ul>
          {Object.keys(responses).map((index, i) => (
            <li key={i}>
              <strong>{index}:</strong>
              {typeof responses[index] === "object"
                ? Object.keys(responses[index]).map(
                    (value) => responses[index][value],
                  )
                : responses[index]}
            </li>
          ))}
        </ul>
      </code>
    );
  };

  return (
    <>
      <LeadFormContext.Provider
        value={{
          steps: steps,
          step: step,
          setStep: setStepFunc,
          responses: responses,
          setResponses: setResponses,
          utms: utms,
          setUTMs: setUTMs,
          userPath: userPath,
          setUserPath: setUserPath,
          sessionKey: sessionKey,
          setSessionKey: setSessionKey,
          loading: loading,
          setLoading: setLoading,
          updateValue: updateValue,
        }}
      >
        <div className="OfferCalculator">
          {loading && (
            <div className="loader">
              <FontAwesomeIcon icon={faSpinner} />
            </div>
          )}

          <Progress step={step} totalSteps={steps.length} />

          <ErrorBoundary>{renderStep()}</ErrorBoundary>

          <div className="Buttons">
            {renderNextButton()}
            {renderBackButton()}
            {renderAdditionalButtons()}
          </div>
        </div>
        {debug && printResponses()}
      </LeadFormContext.Provider>
    </>
  );
};

export default Leadform;
